import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  deleteDataset,
  formatBytes,
  getDataset,
  getPreview,
  listVersions,
  renameDataset,
  retryVersion,
  type Dataset,
  type DatasetVersion,
  type Preview,
} from '../api/datasets';
import { UploadDropzone } from '../components/datasets/UploadDropzone';
import { VersionStatusBadge } from '../components/datasets/VersionStatusBadge';
import { QuickBrewCard } from '../components/dashboards/QuickBrewCard';
import type { Visibility } from '../api/visibility';
import { VisibilityControl, VisibilityBadge } from '../components/VisibilityControl';
import { AppShell } from '../components/layout/AppShell';
import {
  Alert,
  Button,
  Card,
  DataTable,
  ErrorState,
  Input,
  LoadingState,
  type Column,
} from '../components/ui';
import { usePollWhile } from '../hooks/usePollWhile';
import './DatasetDetailPage.css';

type State = 'loading' | 'ready' | 'error';

export function DatasetDetailPage() {
  const { datasetId = '' } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState<State>('loading');
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [versions, setVersions] = useState<DatasetVersion[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>();
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | undefined>();

  const load = useCallback(async () => {
    try {
      const [loaded, loadedVersions] = await Promise.all([
        getDataset(datasetId),
        listVersions(datasetId),
      ]);
      setDataset(loaded);
      setName(loaded.name);
      setVersions(loadedVersions);
      setState('ready');

      // The first rows are the owner's: a public dataset describes itself,
      // and what a reader may take out of it is Phase 15's question.
      const latest = loadedVersions[0];
      if (loaded.is_mine && latest?.status === 'READY') {
        setPreview(await getPreview(datasetId, latest.id, 20));
      } else {
        setPreview(null);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, [datasetId]);

  useEffect(() => {
    void load();
  }, [load]);

  const latest = versions[0];
  const isProcessing = latest?.status === 'PENDING' || latest?.status === 'PROCESSING';

  usePollWhile(isProcessing, load);

  /** Ask for a version that did not finish to be processed again (Phase 32). */
  async function retry(versionId: string) {
    setRetrying(true);
    setRetryError(undefined);
    try {
      await retryVersion(datasetId, versionId);
      // Straight back to the loading state the upload had: the page polls
      // while a version is pending, so it will say when this one lands.
      await load();
    } catch (caught) {
      setRetryError(caught instanceof ApiError ? caught.message : 'We could not queue that again.');
    } finally {
      setRetrying(false);
    }
  }

  async function handleRename() {
    setIsSaving(true);
    setNameError(undefined);
    try {
      setDataset(await renameDataset(datasetId, name));
    } catch (caught) {
      const details = caught instanceof ApiError ? caught.details : {};
      const message = details['name'];
      setNameError(Array.isArray(message) ? message.join(' ') : 'That name was not accepted.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Delete this dataset and every version of it? This cannot be undone.')) {
      return;
    }
    await deleteDataset(datasetId);
    void navigate('/datasets', { replace: true });
  }

  if (state === 'loading') {
    return (
      <AppShell>
        <LoadingState label="Loading dataset…" />
      </AppShell>
    );
  }

  if (state === 'error' || !dataset) {
    return (
      <AppShell>
        <ErrorState
          title="This dataset is not available"
          description={error?.message ?? 'It may have been deleted, or it is not yours.'}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  const schemaColumns: Column<DatasetVersion['column_schema'][number]>[] = [
    { key: 'name', header: 'Column', render: (column) => <code>{column.name}</code> },
    { key: 'source', header: 'In your file', render: (column) => column.source_name },
    { key: 'type', header: 'Type', render: (column) => column.type },
    {
      key: 'nullable',
      header: 'Empty values',
      render: (column) => (column.nullable ? 'Some' : 'None'),
    },
  ];

  const previewColumns: Column<Record<string, unknown>>[] =
    preview?.columns.map((column) => ({
      key: column.name,
      header: column.source_name,
      numeric: column.type === 'integer' || column.type === 'float',
      render: (row) => {
        const value = row[column.name];
        if (value === null || value === undefined) {
          return <span className="sc-table__null">empty</span>;
        }
        // A date-typed column has no meaningful time of day; showing
        // "2026-01-05T00:00:00" is noise.
        if (column.type === 'date' && typeof value === 'string') {
          return value.slice(0, 10);
        }
        return String(value);
      },
    })) ?? [];

  return (
    <AppShell
      header={
        <div className="sc-page-header">
          <div>
            <h1>{dataset.name}</h1>
            <p>
              <VisibilityBadge value={dataset.visibility} /> {dataset.version_count} version
              {dataset.version_count === 1 ? '' : 's'}
              {latest?.status === 'READY' && (
                <>
                  {' · '}
                  {latest.row_count.toLocaleString()} rows · {latest.column_count} columns
                </>
              )}
            </p>
          </div>
          <div className="sc-detail__actions">
            {latest && <VersionStatusBadge status={latest.status} />}
            {latest?.status === 'READY' && (
              <Link to={`/datasets/${datasetId}/profile`}>
                <Button size="sm" variant="secondary">
                  Explore the data
                </Button>
              </Link>
            )}
            {dataset.is_mine && latest?.status === 'READY' && (
              <Link to={`/datasets/${datasetId}/prepare`}>
                <Button size="sm">Prepare</Button>
              </Link>
            )}
            {dataset.is_mine && (
              <Button variant="danger" size="sm" onClick={() => void handleDelete()}>
                Delete
              </Button>
            )}
          </div>
        </div>
      }
    >
      {isProcessing && (
        <Alert tone="info" title="Still brewing">
          Simple Coffee is validating and preparing this file. This page updates itself.
        </Alert>
      )}

      {latest?.status === 'FAILED' && (
        <Alert tone="danger" title="This file could not be processed">
          <p>{latest.error_message}</p>
          {dataset.is_mine && (
            <>
              {/* The bytes are still here and the work is idempotent, so the
                  answer to "it fell over" is not "upload it again" (Phase 32). */}
              <Button size="sm" onClick={() => void retry(latest.id)} disabled={retrying}>
                {retrying ? 'Trying again…' : 'Try again'}
              </Button>
              {retryError && (
                <p className="sc-dataset__retry-error" role="status">
                  {retryError}
                </p>
              )}
            </>
          )}
        </Alert>
      )}

      {dataset.is_mine && latest?.status === 'READY' && <QuickBrewCard versionId={latest.id} />}

      {latest?.status === 'READY' && preview && (
        <Card
          title="Preview"
          description={`First ${preview.returned} of ${preview.row_count.toLocaleString()} rows.`}
        >
          <DataTable
            caption={`First rows of ${dataset.name}`}
            columns={previewColumns}
            rows={preview.rows}
            rowKey={(_row, index) => String(index)}
          />
        </Card>
      )}

      {latest?.status === 'READY' && (
        <Card title="Columns" description="How Simple Coffee read your file.">
          <DataTable
            caption={`Columns of ${dataset.name}`}
            columns={schemaColumns}
            rows={latest.column_schema}
            rowKey={(column) => column.name}
          />
        </Card>
      )}

      {dataset.is_mine && (
        <VisibilityControl
          kind="datasets"
          id={dataset.id}
          value={dataset.visibility}
          noun="dataset"
          onChanged={(visibility: Visibility) => setDataset({ ...dataset, visibility })}
        />
      )}

      <Card title="Versions" description="Every upload is kept as its own version.">
        <ul className="sc-versions">
          {versions.map((version) => (
            <li key={version.id} className="sc-versions__item">
              <div>
                <span className="sc-versions__number">v{version.version}</span>{' '}
                <span className="sc-versions__file">{version.source_filename}</span>
                <div className="sc-versions__meta">
                  {version.source_format || 'unknown'} · {formatBytes(version.source_bytes)} ·{' '}
                  {new Date(version.created_at).toLocaleString()}
                </div>
              </div>
              <VersionStatusBadge status={version.status} />
            </li>
          ))}
        </ul>
      </Card>

      {dataset.is_mine && (
        <Card title="Upload a new version" description="The existing versions stay untouched.">
          <UploadDropzone datasetId={datasetId} onUploaded={() => void load()} />
        </Card>
      )}

      {dataset.is_mine && (
        <Card title="Settings">
          <div className="sc-detail__settings">
            <Input
              label="Dataset name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              error={nameError}
            />
            <Button onClick={() => void handleRename()} isLoading={isSaving} loadingLabel="Saving">
              Save name
            </Button>
          </div>
        </Card>
      )}
    </AppShell>
  );
}
